import { Button, Modal } from 'stories/components'
import { formatDate } from 'utils'

import { DAY_BASIS_LABEL, paymentQuotes } from './paymentOptions'

/**
 * Standard servicing payment options for the Principal & Interest field.
 * Computes the monthly payment for each method from the loan's rate, term and
 * balance, and lets the user apply one to the P&I field.
 *
 * The arithmetic lives in `./paymentOptions`, where it is unit-tested: a figure picked here is
 * stored as the loan's P&I, so it has to agree with the server's own rule for a scheduled payment.
 */
const num = (v: any) => Number(String(v ?? '').replace(/,/g, '')) || 0
const money = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const PaymentOptionsModal = (props: {
  data: Record<string, any>
  onClose: () => void
  onSelect: (value: number) => void
}) => {
  const { data } = props

  const annualRate = num(data.currentInterestRate) || num(data.originalInterestRate)
  // Loan term (number of monthly payments) is derived from the maturity date:
  // whole months from the loan start (closing / first payment) to maturity.
  // Falls back to the Term (Months) field when the dates aren't available.
  const maturityDate = data.curMatDate || data.originalMatDate
  const startDate = data.closingDate || data.firstPaymentDate
  const monthsBetween = (start?: string, end?: string) => {
    if (!start || !end) return 0
    const s = new Date(start)
    const e = new Date(end)
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0
    return Math.max(0, (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()))
  }
  const term = monthsBetween(startDate, maturityDate) || num(data.termMonths)
  const originalAmount = num(data.originalLoanAmount)
  const currentUpb = num(data.currentUpb)

  const { options, basisKey, days, accrual, accrualDiffers } = paymentQuotes({
    annualRate,
    term,
    originalAmount,
    currentUpb,
    isNonDutch: data.interestType === 'Non Dutch',
    dailyRateBasis: data.dailyRateBasis,
    nextPaymentDate: data.nextPaymentDate,
  })
  const basisLabel = DAY_BASIS_LABEL[basisKey] || basisKey

  const row = (label: string, value: string) => (
    <div className="flex justify-between gap-6 py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )

  return (
    <Modal title="Payment Options" isOpen onClose={props.onClose} titleOkay="" titleCancel="Close">
      <div className="w-full max-w-[560px] text-sm">
        <div className="mb-4 rounded bg-gray-50 p-3">
          {row('Interest Rate', annualRate ? `${annualRate}%` : '—')}
          {row('Daily Rate Basis', basisLabel)}
          {accrualDiffers && row(`Accrues this period (${days} days)`, `$${money(Number(accrual.toFixed(2)))}`)}
          {row('Maturity Date', maturityDate ? formatDate(maturityDate) : '—')}
          {row('Term (Months)', term ? String(term) : '—')}
          {row('Original Loan Amount', originalAmount ? `$${money(originalAmount)}` : '—')}
          {currentUpb > 0 && row('Current UPB', `$${money(currentUpb)}`)}
        </div>

        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="py-2 font-medium">Payment Type</th>
              <th className="py-2 font-medium">Based On</th>
              <th className="py-2 text-right font-medium">Monthly P&amp;I</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {options.map((o, i) => (
              <tr key={i} className="border-b">
                <td className="py-2">{o.method}</td>
                <td className="py-2 text-gray-500">{o.basis}</td>
                <td className="py-2 text-right font-medium">
                  {o.amount == null ? '—' : `$${money(Number(o.amount.toFixed(2)))}`}
                </td>
                <td className="py-2 text-right">
                  <Button
                    link
                    disabled={o.amount == null}
                    onClick={() => props.onSelect(Number((o.amount as number).toFixed(2)))}
                  >
                    Use
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {accrualDiffers && (
          <p className="mt-3 text-xs text-gray-500">
            The installment is a twelfth of a year, so it is the same every month. The {basisLabel} basis decides what
            the loan accrues between dates, which is what a payoff or an odd-days quote is struck on, not what the
            borrower is billed each period.
          </p>
        )}

        {!annualRate && (
          <p className="mt-3 text-xs text-red-500">
            Set the Current Interest Rate in Rate &amp; Terms to compute payment amounts.
          </p>
        )}
        {!term && (
          <p className="mt-1 text-xs text-red-500">
            Set the Maturity Date (or Term) to compute the fully-amortized payment.
          </p>
        )}
      </div>
    </Modal>
  )
}
